import { z } from "zod";

import { PredictFromDrawingRequest } from "../contracts/prediction";
import { zMaterialGrade } from "../contracts/pyplan/zod.gen";
import { ExtendedPart } from "../types";
import type { Part as PyplanPart, PartType } from "../contracts/pyplan";

export const Analyze2DJobResultDtoSchema = z
  .object({
    material_description: z.string().nullable(),
    material_grade: z.string().nullable(),
    weight: z.number().nullable(),
    /** @deprecated use configuration_processes instead */
    surface_treatments: z.array(z.string()).optional(),
    part_type: z
      .enum([
        "sheet",
        "consumable",
        "machining",
        "square_tube",
        "round_tube",
        "rectangular_tube",
        "square_bar",
        "round_bar",
        "profile",
        "assembly",
      ])
      .nullable(),
    general_tolerance: z.string().nullable().optional(),
    general_roughness: z.number().nullable().optional(),
    finest_roughness: z.number().nullable().optional(),
    length: z.number().nullable(),
    width: z.number().nullable(),
    height: z.number().nullable(),
    volume: z.number().nullable(),
    manufacturing_process: z.string().nullable().optional(),
    nb_drillings: z.number().nullable().optional(),
    number_of_views: z.number().nullable().optional(),
    /** @deprecated use model_processes instead */
    process_steps: z.array(z.string()).optional(),
    model_processes: z.array(z.string()).optional(),
    configuration_processes: z
      .object({
        surface_treatments: z.array(z.string()).optional(),
      })
      .optional(),
    finest_tolerance: z.number().nullable().optional(),
    finest_tolerance_coverage: z.number().nullable().optional(),
    finest_tolerance_bin_category: z.string().nullable().optional(),
    additional_cost_drivers: z.record(z.unknown()).optional(),

    versions: z
      .object({
        /** @deprecated use pyplan_version instead */
        __pyplan_version__: z.string().optional(),
        /** @deprecated use fabai_version instead */
        __fabai_version__: z.string().optional(),
        /** @deprecated use drawing_analysis_version instead */
        __drawing_analysis_version__: z.string().optional(),
        pyplan_version: z.string().optional(),
        fabai_version: z.string().optional(),
        drawing_analysis_version: z.string().optional(),
      })
      .optional(),

    llm_stats: z
      .object({
        input_tokens: z.number(),
        output_tokens: z.number(),
        total_tokens: z.number(),
        total_cost: z.number(),
      })
      .optional(),

    debug: z.record(z.unknown()).optional(),
  })
  .optional() satisfies z.ZodType<PredictFromDrawingRequest["drawing_extraction"]>;

export type Analyze2DJobResultDto = z.infer<typeof Analyze2DJobResultDtoSchema>;

export function applyPart2DAnalysisFallback(
  analyzed: Analyze2DJobResultDto,
  part: ExtendedPart,
): Analyze2DJobResultDto {
  return {
    ...analyzed,
    material_description: analyzed?.material_description ?? null,
    material_grade: analyzed?.material_grade ?? null,
    volume: part.volume ?? analyzed?.volume ?? null,
    weight: part.weight ?? analyzed?.weight ?? null,
    width: part.width ?? analyzed?.width ?? null,
    height: part.height ?? analyzed?.height ?? null,
    length: part.length ?? analyzed?.length ?? null,
    part_type:
      (part.type
        ? (part.type.toLowerCase() as NonNullable<Analyze2DJobResultDto>["part_type"])
        : null) ??
      analyzed?.part_type ??
      ("sheet" as const),
    versions: analyzed?.versions
      ? {
          pyplan_version: analyzed.versions.__pyplan_version__ ?? analyzed.versions.pyplan_version,
          fabai_version: analyzed.versions.__fabai_version__ ?? analyzed.versions.fabai_version,
          drawing_analysis_version:
            analyzed.versions.__drawing_analysis_version__ ??
            analyzed.versions.drawing_analysis_version,
        }
      : undefined,
  };
}

const LEGACY_PART_TYPE_TO_PART_TYPE: Record<
  NonNullable<NonNullable<Analyze2DJobResultDto>["part_type"]>,
  PartType
> = {
  sheet: "SHEET",
  consumable: "CONSUMABLE",
  machining: "MACHINING",
  square_tube: "SQUARE_TUBE",
  round_tube: "ROUND_TUBE",
  rectangular_tube: "RECTANGULAR_TUBE",
  square_bar: "SQUARE_BAR",
  round_bar: "ROUND_BAR",
  profile: "PROFILE",
  assembly: "ASSEMBLY",
};

/**
 * Bridges the legacy analyze2DJson format to the current PartJson shape, for parts that were
 * analyzed before the migration to the unified partJson format and never got a partJson.
 */
export function mapAnalyze2DJsonToPartJson(
  analyzed: Analyze2DJobResultDto,
  part: ExtendedPart,
): PyplanPart {
  const withFallback = applyPart2DAnalysisFallback(analyzed, part);

  const { length, width, height, volume } = withFallback ?? {};
  const dimensionsPart =
    length && width && height
      ? { type: "square_bar" as const, length, width, height, volume: volume ?? null }
      : null;

  const roughnesses = [withFallback?.finest_roughness, withFallback?.general_roughness]
    .filter((value): value is number => typeof value === "number")
    .filter((value, index, values) => values.indexOf(value) === index)
    .map((value) => ({ value }));

  return {
    source: "2d",
    version: { part_version: "legacy" },
    metadata: {
      material_description: withFallback?.material_description ?? null,
      material_grade: zMaterialGrade.safeParse(withFallback?.material_grade).data ?? null,
      material_density: null,
      part_type: withFallback?.part_type
        ? LEGACY_PART_TYPE_TO_PART_TYPE[withFallback.part_type]
        : null,
      shape_name: null,
      shape_nb: null,
      group: null,
      group_size: null,
      weight: withFallback?.weight ?? null,
      weight_unit: "kg",
      distance_unit: "mm",
    },
    dimensions: {
      bbox:
        part.boundingBoxX && part.boundingBoxY && part.boundingBoxZ
          ? {
              length: part.boundingBoxX,
              width: part.boundingBoxY,
              height: part.boundingBoxZ,
            }
          : null,
      part: dimensionsPart,
    },
    complexity: {
      topo: null,
      roughnesses: roughnesses.length ? roughnesses : null,
      gdts: null,
      measures: null,
    },
  };
}
